import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Briefcase, CheckCircle, Clock, AlertTriangle, Activity, Sparkles,
  ArrowRight, CheckCircle2, ShieldAlert, AlertCircle, FileText, Send
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { getRoleDayPrefix } from '../../../helper/role-helper';

const InternOverview = () => {
  const user = JSON.parse(localStorage.getItem('portalUser')) || {};
  const [tasks, setTasks] = useState([]);
  const [todayData, setTodayData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('portalToken') || localStorage.getItem('admin');
        const headers = { Authorization: `Bearer ${token}` };

        const [tasksRes, todayRes] = await Promise.all([
          axios.get(`${window.API_URL}/api/portal/shared/tasks`, { headers }),
          axios.get(`${window.API_URL}/api/portal/shared/daily-tasks/today`, { headers }).catch(() => ({ data: null }))
        ]);

        setTasks(tasksRes.data || []);
        setTodayData(todayRes.data || null);
      } catch (err) {
        console.error("Error loading intern overview data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.status === 'Completed').length;
  const pendingTasks = tasks.filter(t => t.status === 'Pending' || t.status === 'In Progress').length;
  const underReviewTasks = tasks.filter(t => t.status === 'Submitted' || t.status === 'Under Review').length;
  const revisionsNeeded = tasks.filter(t => t.status === 'Changes Requested').length;
  const overdueTasks = tasks.filter(t => t.deadline && new Date(t.deadline) < new Date() && t.status !== 'Completed').length;
  const productivity = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  const todayTasks = todayData?.tasks || [];
  const todayMetrics = todayData?.metrics || {
    totalTasks: todayTasks.length,
    completedTasks: todayTasks.filter(t => t.status === 'Completed').length,
    completionRate: todayTasks.length > 0 ? Math.round((todayTasks.filter(t => t.status === 'Completed').length / todayTasks.length) * 100) : 0,
    dayStatus: 'No Assignment'
  };

  const roleDayPrefix = getRoleDayPrefix(todayData?.user || user);

  return (
    <div className="space-y-6 text-gray-900 pb-12">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-[#f06600]/15 via-amber-50 to-orange-50/50 rounded-3xl p-6 sm:p-8 border border-orange-200/60 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {todayData?.internshipDay && (
              <span className="bg-[#f06600] text-white text-xs font-black uppercase px-3 py-1 rounded-full shadow-sm">
                {roleDayPrefix} Day {todayData.internshipDay}
              </span>
            )}
            <span className="text-xs font-semibold text-gray-500">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">
            Welcome back, {user.fullName || 'Team Member'}!
          </h2>
          <p className="text-gray-600 text-sm mt-1">
            Track your daily tasks, submit deliverables, and check your automatic attendance status.
          </p>
        </div>

        <Link
          to="/dashboard/intern/tasks"
          className="self-start md:self-auto inline-flex items-center gap-2 px-5 py-3 bg-[#f06600] hover:bg-[#d05800] text-white rounded-2xl font-bold text-sm transition-all shadow-md active:scale-95"
        >
          <Sparkles className="w-4 h-4" /> Go to Today's Tasks <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Today's Agenda & Attendance Mission Card */}
      <div className="bg-white border border-gray-200 rounded-3xl p-6 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="flex-1 space-y-3">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-[#f06600] animate-ping" />
              <h3 className="text-xl font-bold text-gray-900">Today's Daily Mission</h3>
              {todayData?.internshipDay && (
                <span className="text-xs text-gray-500 font-semibold">({roleDayPrefix} Day {todayData.internshipDay})</span>
              )}
            </div>

            <p className="text-sm text-gray-600 leading-relaxed">
              {todayTasks.length === 0 ? (
                "You don't have any specific tasks assigned for today. Attendance is safe with No Assignment."
              ) : (
                <>
                  You have <strong className="text-gray-900">{todayTasks.length} task{todayTasks.length !== 1 ? 's' : ''}</strong> assigned for today. Complete all of them to automatically turn today's attendance to <strong className="text-emerald-700">Present</strong>.
                </>
              )}
            </p>

            {/* Progress Bar */}
            {todayTasks.length > 0 && (
              <div className="space-y-1.5 max-w-xl">
                <div className="flex justify-between text-xs font-bold text-gray-700">
                  <span>Day Completion Rate</span>
                  <span className="text-[#f06600]">{todayMetrics.completedTasks} of {todayMetrics.totalTasks} Tasks ({todayMetrics.completionRate}%)</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 ${
                      todayMetrics.completionRate === 100
                        ? 'bg-gradient-to-r from-emerald-500 to-green-500'
                        : 'bg-gradient-to-r from-[#f06600] to-amber-500'
                    }`}
                    style={{ width: `${todayMetrics.completionRate}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Attendance State Box */}
          <div className="bg-gray-50 rounded-2xl p-5 border border-gray-200/80 min-w-[260px] flex flex-col justify-center">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider block mb-1.5">
              Today's Attendance Status
            </span>
            <div className="flex items-center gap-2">
              {todayData?.attendance?.status === 'Present' ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-extrabold bg-emerald-100 text-emerald-800 border border-emerald-300">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Present (Auto-Marked)
                </span>
              ) : todayData?.attendance?.source === 'admin_override' ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-extrabold bg-amber-100 text-amber-900 border border-amber-300">
                  <ShieldAlert className="w-4 h-4 text-amber-600" /> Overridden: {todayData.attendance.status}
                </span>
              ) : todayTasks.length === 0 ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-gray-200 text-gray-700">
                  No Assignment
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-extrabold bg-yellow-100 text-yellow-900 border border-yellow-300">
                  <Clock className="w-4 h-4 text-yellow-600" /> Incomplete / Pending
                </span>
              )}
            </div>
            {todayData?.attendance?.overrideReason && (
              <p className="text-[11px] text-gray-500 italic mt-2">
                "{todayData.attendance.overrideReason}"
              </p>
            )}
          </div>
        </div>

        {/* Changes Requested Warning */}
        {revisionsNeeded > 0 && (
          <div className="mt-4 p-4 bg-orange-50 border-2 border-orange-300 rounded-2xl flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-600 shrink-0" />
              <div>
                <p className="text-xs sm:text-sm font-bold text-orange-950">
                  {revisionsNeeded} task{revisionsNeeded !== 1 ? 's require' : ' requires'} revision!
                </p>
                <p className="text-xs text-orange-800">
                  Your mentor requested changes. Please check feedback and resubmit your deliverables.
                </p>
              </div>
            </div>
            <Link
              to="/dashboard/intern/tasks"
              className="px-3.5 py-1.5 bg-orange-600 hover:bg-orange-700 text-white rounded-xl text-xs font-bold transition-colors shrink-0 shadow-sm"
            >
              Review Now
            </Link>
          </div>
        )}
      </div>

      {/* KPI Counters */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <Link to="/dashboard/intern/tasks">
          <StatCard
            icon={<Sparkles className="w-5 h-5 text-[#f06600]" />}
            title="Today's Tasks"
            value={todayTasks.length}
            sub={`${todayMetrics.completedTasks} done`}
          />
        </Link>
        <Link to="/dashboard/intern/tasks">
          <StatCard
            icon={<CheckCircle className="w-5 h-5 text-emerald-600" />}
            title="Completed"
            value={completedTasks}
            sub="All time"
          />
        </Link>
        <Link to="/dashboard/intern/tasks">
          <StatCard
            icon={<Clock className="w-5 h-5 text-blue-600" />}
            title="Active / Pending"
            value={pendingTasks}
            sub="In progress"
          />
        </Link>
        <Link to="/dashboard/intern/tasks">
          <StatCard
            icon={<Send className="w-5 h-5 text-purple-600" />}
            title="Under Review"
            value={underReviewTasks}
            sub="Awaiting admin"
          />
        </Link>
        <Link to="/dashboard/intern/tasks">
          <StatCard
            icon={<AlertTriangle className="w-5 h-5 text-rose-600" />}
            title="Overdue"
            value={overdueTasks}
            sub="Past deadline"
          />
        </Link>
        <Link to="/dashboard/intern/tasks">
          <StatCard
            icon={<Activity className="w-5 h-5 text-[#f06600]" />}
            title="Productivity"
            value={`${productivity}%`}
            sub="Completion rate"
          />
        </Link>
      </div>

      {/* Today's Tasks Checklist & Recent Tasks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today's Task Checklist */}
        <div className="bg-white shadow-sm rounded-3xl p-6 border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Today's Task List</h3>
              <p className="text-xs text-gray-500">Tasks assigned for today's working session</p>
            </div>
            <Link to="/dashboard/intern/tasks" className="text-xs font-bold text-[#f06600] hover:underline">
              Open Full View &rarr;
            </Link>
          </div>

          <div className="space-y-3">
            {todayTasks.map(task => (
              <div
                key={task._id}
                className="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex justify-between items-center gap-3 hover:border-[#f06600]/30 transition-all"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {task.category && (
                      <span className="text-[10px] bg-white text-gray-700 font-bold px-2 py-0.5 rounded-full border border-gray-200">
                        {task.category}
                      </span>
                    )}
                    <span className="text-[10px] font-semibold text-gray-500">{task.priority} Priority</span>
                  </div>
                  <p className="font-bold text-gray-900 text-sm truncate">{task.title}</p>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-bold shrink-0 ${
                  task.status === 'Completed' ? 'bg-emerald-100 text-emerald-700' :
                  task.status === 'Submitted' ? 'bg-purple-100 text-purple-700' :
                  task.status === 'Changes Requested' ? 'bg-orange-100 text-orange-800' :
                  task.status === 'In Progress' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {task.status}
                </span>
              </div>
            ))}
            {todayTasks.length === 0 && (
              <div className="py-8 text-center text-gray-400">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs font-semibold">No tasks assigned for today</p>
              </div>
            )}
          </div>
        </div>

        {/* All Recent Task Activity */}
        <div className="bg-white shadow-sm rounded-3xl p-6 border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Recent Tasks</h3>
              <p className="text-xs text-gray-500">Latest assigned tasks across all days</p>
            </div>
            <Link to="/dashboard/intern/tasks" className="text-xs font-bold text-[#f06600] hover:underline">
              View All &rarr;
            </Link>
          </div>

          <div className="space-y-3">
            {tasks.slice(0, 5).map(task => (
              <Link
                key={task._id}
                to="/dashboard/intern/tasks"
                className="flex justify-between items-center p-3.5 bg-gray-50 rounded-2xl border border-gray-100 hover:border-[#f06600]/30 transition-all block"
              >
                <div className="min-w-0 flex-1 pr-3">
                  <p className="font-bold text-gray-900 text-sm truncate">{task.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {task.internshipDay ? `${roleDayPrefix} Day ${task.internshipDay} • ` : ''}
                    Deadline: {task.deadline ? new Date(task.deadline).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-bold shrink-0 ${
                  task.status === 'Completed' ? 'bg-emerald-100 text-emerald-700' :
                  task.status === 'Submitted' ? 'bg-purple-100 text-purple-700' :
                  task.status === 'Changes Requested' ? 'bg-orange-100 text-orange-800' :
                  task.status === 'In Progress' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {task.status}
                </span>
              </Link>
            ))}
            {tasks.length === 0 && (
              <div className="py-8 text-center text-gray-400">
                <p className="text-xs font-semibold">No recent activity.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, title, value, sub }) => (
  <div className="bg-white shadow-sm rounded-2xl p-4 border border-gray-200 hover:border-[#f06600]/40 transition-all flex flex-col justify-between">
    <div className="flex items-center justify-between mb-2">
      <div className="p-2 bg-gray-50 rounded-xl">{icon}</div>
      {sub && <span className="text-[10px] font-semibold text-gray-400">{sub}</span>}
    </div>
    <div>
      <h4 className="text-2xl font-black text-gray-900 tracking-tight">{value}</h4>
      <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mt-0.5">{title}</p>
    </div>
  </div>
);

export default InternOverview;
